import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  // Initialize theme from localStorage or default to dark
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('string-sauce-theme');
      return savedTheme || 'dark';
    }
    return 'dark';
  });

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'light') {
      // Light theme CSS variables
      root.style.setProperty('--background', '0 0% 98%');
      root.style.setProperty('--foreground', '0 0% 9%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '0 0% 9%');
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '0 0% 9%');
      root.style.setProperty('--muted', '0 0% 96%');
      root.style.setProperty('--muted-foreground', '0 0% 45%');
      root.style.setProperty('--accent', '0 0% 96%');
      root.style.setProperty('--accent-foreground', '0 0% 9%');
      root.style.setProperty('--border', '0 0% 90%');
      root.style.setProperty('--input', '0 0% 90%');
      root.classList.remove('dark');
      root.classList.add('light');
    } else {
      // Dark theme CSS variables (original)
      root.style.setProperty('--background', '0 0% 2%');
      root.style.setProperty('--foreground', '0 0% 98%');
      root.style.setProperty('--card', '0 0% 4%');
      root.style.setProperty('--card-foreground', '0 0% 98%');
      root.style.setProperty('--popover', '0 0% 4%');
      root.style.setProperty('--popover-foreground', '0 0% 98%');
      root.style.setProperty('--muted', '0 0% 10%');
      root.style.setProperty('--muted-foreground', '240 4% 46%');
      root.style.setProperty('--accent', '0 0% 10%');
      root.style.setProperty('--accent-foreground', '0 0% 98%');
      root.style.setProperty('--border', '0 0% 14%');
      root.style.setProperty('--input', '0 0% 14%');
      root.classList.remove('light');
      root.classList.add('dark');
    }
    
    // Save to localStorage
    localStorage.setItem('string-sauce-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export default ThemeContext;
